import { apiClient, objectToQueryString } from "@wtf/shared/api/utils/api-client";
import {
  CollectionDto,
  CollectionProductsResponse,
  CreateCollectionRequest,
  UpdateCollectionRequest,
  AddProductToCollectionRequest,
  AddLookToCollectionRequest,
} from "@wtf/shared";

const BASE_ENDPOINT = "api/collections";

/**
 * Get archived collections belonging to the current user
 */
export async function getArchivedCollections(
  params: { limit?: number; offset?: number } = {}
): Promise<CollectionDto[]> {
  const queryString = objectToQueryString(params);
  return apiClient<CollectionDto[]>(`${BASE_ENDPOINT}/archived${queryString}`);
}

/**
 * Create a new collection
 */
export async function createCollection(data: CreateCollectionRequest): Promise<CollectionDto> {
  return apiClient<CollectionDto>(BASE_ENDPOINT, "POST", data);
}

/**
 * Get a collection by ID
 */
export async function getCollectionById(id: string): Promise<CollectionDto> {
  return apiClient<CollectionDto>(`${BASE_ENDPOINT}/${id}`);
}

/**
 * Get paginated products for a collection
 * Used for lazy loading products when viewing collection details
 * @param collectionId The collection ID to fetch products from
 * @param params.limit Maximum number of products to return (default: 50, max: 100)
 * @param params.offset Number of products to skip (for pagination)
 */
export async function getCollectionProducts(
  collectionId: string,
  params: { limit?: number; offset?: number } = {}
): Promise<CollectionProductsResponse> {
  const queryString = objectToQueryString(params);
  return apiClient<CollectionProductsResponse>(`${BASE_ENDPOINT}/${collectionId}/products${queryString}`);
}

/**
 * Update a collection
 */
export async function updateCollection(id: string, data: UpdateCollectionRequest): Promise<CollectionDto> {
  return apiClient<CollectionDto>(`${BASE_ENDPOINT}/${id}`, "PUT", data);
}

/**
 * Delete a collection
 */
export async function deleteCollection(id: string): Promise<void> {
  return apiClient<void>(`${BASE_ENDPOINT}/${id}`, "DELETE");
}

/**
 * Archive a collection
 */
export async function archiveCollection(id: string): Promise<CollectionDto> {
  return apiClient<CollectionDto>(`${BASE_ENDPOINT}/${id}/archive`, "PUT");
}

/**
 * Unarchive a collection
 */
export async function unarchiveCollection(id: string): Promise<CollectionDto> {
  return apiClient<CollectionDto>(`${BASE_ENDPOINT}/${id}/unarchive`, "PUT");
}

/**
 * Add a product to a collection
 */
export async function addProductToCollection(collectionId: string, data: AddProductToCollectionRequest): Promise<void> {
  return apiClient<void>(`${BASE_ENDPOINT}/${collectionId}/products`, "POST", data);
}

/**
 * Remove a product from a collection
 */
export async function removeProductFromCollection(collectionId: string, productId: string): Promise<void> {
  return apiClient<void>(`${BASE_ENDPOINT}/${collectionId}/products/${productId}`, "DELETE");
}

/**
 * Add a look to a collection
 */
export async function addLookToCollection(collectionId: string, data: AddLookToCollectionRequest): Promise<void> {
  return apiClient<void>(`${BASE_ENDPOINT}/${collectionId}/looks`, "POST", data);
}

/**
 * Remove a look from a collection
 */
export async function removeLookFromCollection(collectionId: string, lookId: string): Promise<void> {
  return apiClient<void>(`${BASE_ENDPOINT}/${collectionId}/looks/${lookId}`, "DELETE");
}

/**
 * Save (bookmark) a collection for the current user
 * Returns the updated collection object
 */
export async function saveCollection(id: string): Promise<CollectionDto> {
  return apiClient<CollectionDto>(`${BASE_ENDPOINT}/${id}/save`, "POST");
}

/**
 * Unsave (remove bookmark) a collection for the current user
 * Returns the updated collection object
 */
export async function unsaveCollection(id: string): Promise<CollectionDto> {
  return apiClient<CollectionDto>(`${BASE_ENDPOINT}/${id}/save`, "DELETE");
}

export const collectionsApi = {
  getArchivedCollections,
  createCollection,
  getCollectionById,
  getCollectionProducts,
  updateCollection,
  deleteCollection,
  archiveCollection,
  unarchiveCollection,
  addProductToCollection,
  removeProductFromCollection,
  addLookToCollection,
  removeLookFromCollection,
  saveCollection,
  unsaveCollection,
};
